import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import OrderHeader from './OrderHeader';
import ClientDetailsForm from './ClientDetailsForm';
import OrderStatusSelector from './OrderStatusSelector';
import PlatformSelector from './PlatformSelector';
import DepotSelector from '../components/DepotSelector';
import OrderItemsTable from './OrderItemsTable';
import OrderSummary from './OrderSummary';
import { useOrderForm } from './useOrderForm';
import { createOrder, fetchOrderNewReference } from '../orders-controller/order-controller';
import PhoneOrderHistoryModal from './PhoneOrderHistoryModal'; 

const AddOrder = () => {
    const navigate = useNavigate();
    const [isLoadingReference, setIsLoadingReference] = useState(false);
    const [referenceError, setReferenceError] = useState(null);
    
    const {
        formData,
        orderItems,
        errors,
        totals,
        statusOptions,
        handleInputChange,
        handleStatusChange,
        handleAddItem,
        handleUpdateItem,
        handleRemoveItem,
        handleSubmit,
        isSubmitting,
        setFormData,
        updateTotals,
        promoCodeState,
        handlePromoCodeBlur,
        showPhoneWarning,
        phoneWarningOrders,
        setShowPhoneWarning,
    } = useOrderForm(navigate);

    // Fetch new reference on component mount
    useEffect(() => {
        const getOrderReference = async () => {
            try {
                setIsLoadingReference(true);
                setReferenceError(null);
                
                const response = await fetchOrderNewReference();
                
                if (response && response.numero) {
                    // Update the form data with the new reference
                    setFormData(prev => ({
                        ...prev,
                        reference: response.numero
                    }));
                } else {
                    throw new Error('Invalid reference format received');
                }
            } catch (error) {
                console.error('Failed to fetch order reference:', error);
                setReferenceError('Impossible de générer une référence automatique');
            } finally {
                setIsLoadingReference(false);
            }
        };

        getOrderReference();
    }, [setFormData]);

    // Log errors to console when they change
    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            console.log("Form errors:", errors);
        }
    }, [errors]);

    // Manual submit handler as a backup
    const onSubmitForm = (e) => {
        console.log("Submit button clicked");
        // Call the handleSubmit from the hook
        handleSubmit(e);
    };

    return (
        <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen">
            <OrderHeader />

            <div className="max-w-7xl mx-auto">
                <form onSubmit={onSubmitForm} className="space-y-8">
                    <div className="bg-white overflow-hidden sm:rounded-lg border border-gray-200">
                        {/* Header with gradient background */}
                        <div className="px-6 py-5 sm:px-8 bg-white border-b border-gray-200 flex justify-between items-center">
                            <div className="flex items-center">
                                <div className="p-2 rounded-md bg-[#f5f8f7] mr-4">
                                    <svg className="h-6 w-6 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg leading-6 font-medium text-gray-900">Nouvelle Commande</h3>
                                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                        Créez une nouvelle commande en remplissant les champs ci-dessous
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Form fields section with enhanced styling */}
                        <div className="px-6 py-6 sm:px-8 sm:py-7">
                            <div className="grid grid-cols-1 gap-y-8 gap-x-6 sm:grid-cols-6">
                                {/* Reference field */}
                                <div className="sm:col-span-3">
                                    <label htmlFor="reference" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                        Référence <span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400 group-hover:text-[#174540] transition-colors duration-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                            </svg>
                                        </div>
                                        <input
                                            type="text"
                                            name="reference"
                                            id="reference"
                                            value={isLoadingReference ? 'Génération de référence...' : formData.reference}
                                            onChange={handleInputChange}
                                            readOnly={false}
                                            className={`pl-10 py-3 shadow-sm block w-full border-0 border-b-2 border-gray-200 focus:ring-0 focus:border-[#174540] transition-all duration-300 ease-in-out text-sm rounded-t-md bg-gray-100 ${
                                                referenceError ? 'border-red-300 text-red-900' : ''
                                            }`}
                                        />
                                        {isLoadingReference && (
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            </div>
                                        )}
                                        {referenceError && (
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    {referenceError && <p className="mt-2 text-sm text-red-600">{referenceError}</p>}
                                </div>

                                {/* Rest of your form remains the same */}
                                {/* Date field */}
                                <div className="sm:col-span-3">
                                    <label htmlFor="dateBonCommande" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                        Date de commande <span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400 group-hover:text-[#174540] transition-colors duration-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <input
                                            type="datetime-local"
                                            name="dateBonCommande"
                                            id="dateBonCommande"
                                            value={formData.dateBonCommande}
                                            onChange={handleInputChange}
                                            className={`pl-10 py-3 shadow-sm block w-full border-0 border-b-2 border-gray-200 focus:ring-0 focus:border-[#174540] transition-all duration-300 ease-in-out text-sm rounded-t-md bg-gray-50 ${errors.dateBonCommande ? 'border-red-300 text-red-900 placeholder-red-300' : ''
                                                }`}
                                        />
                                        {errors.dateBonCommande && (
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    {errors.dateBonCommande && <p className="mt-2 text-sm text-red-600">{errors.dateBonCommande}</p>}
                                </div>

                                {/* Status selector component */}
                                <div className="sm:col-span-3">
                                    <OrderStatusSelector
                                        status={formData.etat}
                                        onChange={handleStatusChange}
                                        options={statusOptions}
                                        error={errors.etat}
                                    />
                                </div>

                                {/* Platform selector component */}
                                <div className="sm:col-span-3">
                                    <PlatformSelector
                                        selectedPlatform={formData.platformId}
                                        onChange={handleInputChange}
                                        error={errors.platformId}
                                    />
                                </div>

                                {/* Depot selector */}
                                <div className="sm:col-span-3">
                                    <DepotSelector
                                        value={formData.depotId}
                                        onChange={handleInputChange}
                                        error={errors.depotId}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Rest of your form components */}
                    <ClientDetailsForm
                        formData={formData}
                        onChange={handleInputChange}
                        errors={errors}
                        promoCodeState={promoCodeState}
                        onPromoCodeBlur={handlePromoCodeBlur}
                        isEditMode={false} // NEW - explicitly pass false for create mode
                    />

                    <OrderItemsTable
                        items={orderItems}
                        onAddItem={handleAddItem}
                        onUpdateItem={handleUpdateItem}
                        onRemoveItem={handleRemoveItem}
                        totals={totals}
                        onUpdateTotals={updateTotals}
                    />

                    <OrderSummary totals={totals} />

                    <div className="flex justify-end space-x-3 pt-5 border-t border-gray-200">
                        <Link
                            to="/admin/orders"
                            className="py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                        >
                            Annuler
                        </Link>
                        <button
                            type="submit"
                            disabled={isSubmitting || isLoadingReference}
                            onClick={(e) => {
                                console.log("Submit button clicked directly");
                                onSubmitForm(e);
                            }}
                            className={`py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] ${(isSubmitting || isLoadingReference) ? 'opacity-75 cursor-not-allowed' : ''
                                }`}
                        >
                            {isSubmitting ? (
                                <div className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Enregistrement...
                                </div>
                            ) : (
                                'Enregistrer la commande'
                            )}
                        </button>
                    </div>
                </form>
            </div>
            
            {/* Debug info */}
            {Object.keys(errors).length > 0 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
                    <h4 className="text-red-800 font-medium">Erreurs de validation:</h4>
                    <ul className="mt-2 text-sm text-red-700">
                        {Object.entries(errors).map(([key, value]) => (
                            <li key={key}>{key}: {value}</li>
                        ))}
                    </ul>
                </div>
            )}
            <PhoneOrderHistoryModal
                open={showPhoneWarning}
                onClose={() => setShowPhoneWarning(false)}
                orders={phoneWarningOrders}
                phone={formData.client.phone}
            />
        </div>
    );
};

export default AddOrder;